import React, { type ReactNode, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { CustomThemeButton } from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { MountEvent } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

export type Props = {
	isDisabled?: boolean;
	isLoading?: boolean;
	isInitialLoading: boolean;
	tooltipContent: string;
	onMount: (arg1: UIAnalyticsEvent) => void;
	onClick: (arg1: SyntheticEvent<HTMLElement>, arg2: UIAnalyticsEvent) => void;
	forwardRef?: React.Ref<HTMLElement>;
};

type ViewProps = Props;

const WithTooltip = ({ content, children }: { content: string; children: ReactNode }) => {
	if (content) {
		return (
			<Tooltip tag="span" content={content}>
				{children}
			</Tooltip>
		);
	}
	return <>{children}</>;
};

const ViewWorkflowButton = ({
	forwardRef,
	isDisabled,
	isLoading,
	isInitialLoading,
	onClick,
	onMount,
	tooltipContent,
}: ViewProps) => {
	const intl = useIntl();
	const isDisabledColor = isDisabled === true && !isInitialLoading;

	return (
		<WrapperWithoutBorder>
			<MountEvent onMount={onMount} />
			<WithTooltip content={tooltipContent}>
				<ButtonWrapper>
					<CustomThemeButton
						testId="issue-field-status.common.ui.view-workflow.button.view-workflow"
						shouldFitContainer
						appearance="subtle"
						spacing="none"
						isDisabled={isDisabled}
						isLoading={isLoading}
						onClick={onClick}
						theme={(currentTheme, theme) => {
							const buttonTheme = currentTheme(theme);
							return {
								...buttonTheme,
								buttonStyles: {
									...buttonTheme.buttonStyles,
									alignItems: 'center',
									textAlign: 'left',
									color: isDisabledColor ? token('color.text.disabled') : token('color.text'),
									padding: `0 ${token('space.150')}`,
								},
							};
						}}
						interactionName="auto-generated.issue-field-status.common.ui.view-workflow.button.view-workflow.button"
						ref={forwardRef}
					>
						{intl.formatMessage(messages.viewWorkflow)}
					</CustomThemeButton>
				</ButtonWrapper>
			</WithTooltip>
		</WrapperWithoutBorder>
	);
};

export default ViewWorkflowButton;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const WrapperWithoutBorder = styled.div({
	height: token('space.500'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ButtonWrapper = styled.div({
	display: 'flex',
	flexGrow: 1,
	height: '100%',
});
